/**
 * Notification sound — pure WebAudio beep (no external asset needed).
 *
 * Spec rev101 (2026-02) — Le Desk joue un très court chime "bell-like" à
 * l'arrivée d'une notification, à condition que :
 *   • le Premier Admin ait activé le son global,
 *   • ET le type de déclencheur soit activé dans les préférences.
 *
 * L'audio contexte est lazy-instancié au premier appel — les navigateurs
 * modernes exigent un geste utilisateur pour démarrer un AudioContext.
 * Comme la cloche est toujours ouverte au moins une fois par l'utilisateur
 * avant qu'un son ne soit joué (chargement de la page = geste), on est
 * couvert dans 99 % des cas. Si le contexte est bloqué (suspended), on
 * ignore silencieusement — pas d'erreur console visible.
 */
let _ctx = null;

function getCtx() {
  if (_ctx) return _ctx;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    _ctx = new AC();
    return _ctx;
  } catch {
    return null;
  }
}

/**
 * Play a short two-tone bell (E5 → G5, ~180 ms total). Deliberately soft
 * so it doesn't feel jarring in an open-space office.
 */
// Autoplay policy : un AudioContext créé sans geste reste "suspended".
// On le crée/réveille au premier geste utilisateur pour que les beeps
// déclenchés plus tard par le polling soient audibles.
if (typeof window !== "undefined") {
  const unlock = () => {
    const ctx = getCtx();
    if (ctx && ctx.state === "suspended") ctx.resume?.().catch(() => {});
    if (ctx && ctx.state === "running") {
      window.removeEventListener("pointerdown", unlock, true);
      window.removeEventListener("keydown", unlock, true);
    }
  };
  window.addEventListener("pointerdown", unlock, true);
  window.addEventListener("keydown", unlock, true);
}

export function playNotificationBeep() {
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") {
    ctx.resume?.().then(() => _tone(ctx)).catch(() => {});
    return;
  }
  _tone(ctx);
}

function _tone(ctx) {
  try {
    const now = ctx.currentTime;
    const play = (freq, startOffset, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, now + startOffset);
      // Very soft envelope : quick attack, gentle decay.
      gain.gain.setValueAtTime(0.0001, now + startOffset);
      gain.gain.exponentialRampToValueAtTime(0.14, now + startOffset + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + startOffset + duration);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + startOffset);
      osc.stop(now + startOffset + duration + 0.02);
    };
    play(659.25, 0.00, 0.14);  // E5
    play(783.99, 0.09, 0.16);  // G5
  } catch {
    /* silent */
  }
}
